<template>
  <div v-if="accordion" v-editable="blok" class="accordion">
    <div
      v-for="(item, index) in accordion"
      :key="`${index}-${item.title}`"
      class="accordion__item"
    >
      <h3>
        <a
          :id="'label-' + uid + '-' + index"
          :aria-controls="'content-' + uid + '-' + index"
          :aria-expanded="activeAccordion === index"
          class="flex justify-between align-center"
          @click="toggleAccordion(index)"
        >
          <span>
            {{ item.title }}
          </span>
          <i v-if="activeAccordion !== index" class="icon">
            <Icon name="fa6-solid:plus" />
          </i>
          <i v-if="activeAccordion === index" class="icon">
            <Icon name="fa6-solid:minus" />
          </i>
        </a>
      </h3>
      <transition
        name="accordion"
        @after-enter="transitionAfterEnter"
        @after-leave="transitionAfterLeave"
        @before-enter="transitionBeforeEnter"
        @before-leave="transitionBeforeLeave"
        @enter="transitionEnter"
        @leave="transitionLeave"
      >
        <div
          v-show="activeAccordion === index"
          :id="'content-' + uid + '-' + index"
          :aria-labelledby="'label-' + uid + '-' + index"
          :class="[item.rtl_text ? 'rtl' : '']"
          class="accordion__content"
          role="region"
        >
          <RichTextRenderer :document="item.content" />
        </div>
      </transition>
    </div>
  </div>
  <div v-else class="accordion">
    <p class="meta-text">
      <strong> Author Warning: </strong>
      This accordion is empty
    </p>
  </div>
</template>

<script setup lang="ts">
import type { InlineAccordionStoryblok } from '~/types/storyblok'

const props = defineProps<{
  blok: InlineAccordionStoryblok
}>()
const activeAccordion = ref(null)
const scrollAfterDelay = ref(null)
const accordion = computed(() =>
  props.blok.items[0] ? props.blok.items : null,
)
const uid = computed(() => props.blok._uid)
function toggleAccordion(index: number) {
  if (activeAccordion.value === index) {
    activeAccordion.value = null
    return
  }

  activeAccordion.value = index
}

function transitionBeforeEnter(el) {
  el.style.height = '0'
}

function transitionAfterEnter(el) {
  el.style.height = ''
}

function transitionEnter(el) {
  el.style.height = el.scrollHeight + 'px'

  // scroll to opened item
  clearTimeout(scrollAfterDelay.value)
  scrollAfterDelay.value = setTimeout(() => {
    const top = document.getElementById(el.id).offsetTop - 100

    window.scrollTo({
      top,
      behavior: 'smooth',
    })
  }, 350)
}

function transitionBeforeLeave(el) {
  el.style.minHeight = el.scrollHeight + 'px'
  el.style.maxHeight = el.scrollHeight + 'px'
  el.style.height = el.scrollHeight + 'px'
}

function transitionLeave(el) {
  el.style.minHeight = '0'
  el.style.maxHeight = '0'
  el.style.height = '0'
}

function transitionAfterLeave(el) {
  el.style.minHeight = ''
  el.style.maxHeight = ''
}
</script>

<style lang="less" scoped>
.accordion {
  margin: @gutter-xl 0;
  border-bottom: @1px solid @color-border;

  .accordion__item {
    border-top: @1px solid @color-border;
    padding: @gutter-xs 0;

    @media @tablet {
      padding: @gutter @gutter @gutter @gutter-xs;
    }

    h3 {
      .font-lg();
      margin: 0;

      span {
        .block();

        padding-right: @gutter;
      }

      .icon {
        color: @color-secondary;
      }
    }

    .accordion__content {
      overflow: hidden;
      transition: all 0.3s ease-in-out;
      padding-top: @gutter;
      > div {
        padding: @gutter-lg 0 0;
      }

      &.rtl {
        direction: rtl;
      }
    }
  }
}

@media print {
  .accordion {
    .accordion__item {
      padding: @gutter-lg;

      .accordion__content {
        .block() !important;

        height: auto !important;
        padding-left: @gutter-lg;

        h4 {
          .font-md();
        }
      }

      h3 {
        i.icon {
          .hidden();
        }
      }
    }
  }
}
</style>
